// Horisontdeteksjon i bilde — kort MAAL-03: automatiser horisont-referansen
// `maritimAvstand` (maalpeking.ts, bølge MAAL-01) i dag krever et MANUELT
// klikk for, og hent pitch/roll DIREKTE ut av bildet i stedet for fra
// telefonens IMU. Se `vale/MAALPEKING_KONSEPT_2026-08-17.md` §1 og kortet.
//
// ── HVORFOR DENNE FILA FINNES ────────────────────────────────────────────────
// `maalestimat.ts` sier det rett ut (kommentaren over `estimerMaal`): fra 2 m
// øyehøyde er hele måleområdet 0→5,4 km presset inn i 2,54 bueminutter
// depresjon, og IMU-pitch (typisk 1,7° feil) er 40× det budsjettet. Horisont-
// linja selv strekker seg over tusenvis av piksler og gir et linjefit langt
// under ett piksel — DET er referansen som faktisk holder mål over vann.
//
// TO TING UT AV SAMME FIT, IKKE TO ALGORITMER:
//   1. Linjas posisjon der den skjærer bildets SENTERKOLONNE er
//      `yVedSenterPx` — nøyaktig samme referanse operatøren ellers klikker inn
//      manuelt som `horisontYPiksel` i `MalpekingTilstand` (malpeking.ts).
//      Auto-deteksjon ERSTATTER klikket, den introduserer ikke et parallelt
//      datafelt (§3 — én kilde til sannhet).
//   2. Linjas AVVIK fra der geometrien sier den skal være ved pitch=0 (kjent
//      øyehøyde → kjent dip, `horisontDipDeg` i maalpeking.ts) ER pitch.
//      Linjas HELNING (i pikselkoordinater) ER roll. Se
//      `pitchRollFraHorisontlinje`.
//
// ⚠ IKKE STATE-OF-THE-ART BILDEBEHANDLING, OG DET ER MED VILJE (§1: bygg det
// som faktisk kan verifiseres, ikke det som ser mest imponerende ut). Dette
// er klassisk «gradient per kolonne + subpiksel-parabelfit + robust
// linjefit med sigma-klipping» — presist nok til grunnlovens krav og enkelt
// nok til å teste deterministisk med syntetiske, anti-aliasede bilder (se
// testfila). EKTE kamerastøy, skip i horisontlinja, disig sikt, glitter fra
// sola osv. er IKKE evaluert her — bare den syntetiske, hånd-kontrollerte
// geometrien testfila bygger. Rapporter nøyaktigheten som MÅLT der, ikke som
// en generell påstand om ekte feltbilder.
//
// ⚠ GJENBRUK, IKKE DUPLIKAT (§3): `BOLGE_HOYDE_FORHOLD_MIN` og
// `horisontDipDeg` fra `maalpeking.ts` (SAMME terskel/formel `maritimAvstand`
// allerede bruker), `vinkelFraPikselRad` fra `kamerakalibrering.ts` (SAMME
// rektilineære projeksjon, brukt i revers akkurat som landemerke-resection
// gjør med `vinkelFraPikselKolonne`).
import { vinkelFraPikselRad } from './kamerakalibrering.js';
import { horisontDipDeg, BOLGE_HOYDE_FORHOLD_MIN } from './maalpeking.js';
/** Luma (BT.601-vekting) for pikselen ved rad `y` i kolonne `x`. Alpha ignoreres med vilje — sky/hav-kontrasten ligger i RGB, ikke i dekning. */
function luminansKolonne(bilde, x) {
    const { breddePx: w, hoydePx: h, data } = bilde;
    const ut = new Float64Array(h);
    for (let y = 0; y < h; y++) {
        const i = (y * w + x) * 4;
        ut[y] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return ut;
}
/**
 * Finn den STERKESTE lysstyrke-overgangen i én kolonne, med subpiksel-
 * presisjon via parabelinterpolasjon av gradienten rundt toppen — standard
 * subpiksel-kant-teknikk: gitt tre gradientverdier g₋₁,g₀,g₁ rundt et
 * diskret maksimum ved 0, er den sanne toppen 0,5·(g₋₁−g₁)/(g₋₁−2g₀+g₁)
 * unna. `null` når ingen kolonne-overgang er sterk nok til å stole på (flatt
 * parti, jevn dis) — en svak kant er ikke et punkt på horisontlinja, det er
 * støy, og skal IKKE telle med i fittet.
 */
function finnKantIKolonne(lum, minKontrast) {
    const h = lum.length;
    if (h < 5)
        return null;
    const grad = new Float64Array(h);
    let bestY = -1;
    let bestG = 0;
    for (let y = 1; y < h - 1; y++) {
        const g = Math.abs(lum[y + 1] - lum[y - 1]);
        grad[y] = g;
        if (g > bestG) {
            bestG = g;
            bestY = y;
        }
    }
    if (bestY < 1 || bestY > h - 2 || bestG < minKontrast)
        return null;
    const gm = grad[bestY - 1];
    const g0 = grad[bestY];
    const gp = grad[bestY + 1];
    const nevner = gm - 2 * g0 + gp;
    // Klipp forskyvningen til ±1 px: en degenerert nevner (nesten flat topp) skal
    // ikke kunne kaste subpikselpunktet et helt vindu utenfor det diskrete funnet.
    const delta = Math.abs(nevner) > 1e-9 ? Math.max(-1, Math.min(1, (0.5 * (gm - gp)) / nevner)) : 0;
    return { ySub: bestY + delta, styrke: bestG };
}
/**
 * Minste-kvadraters fit av y = m·(x−cx) + b, med residual-σ og en
 * approksimert σ for helningen.
 *
 * ⚠ BEVISST UVEKTET, IKKE VEKTET PÅ GRADIENTSTYRKEN. Første instinkt er å
 * vekte hvert punkt med hvor sterk kanten var (mer «troverdig» kant = mer
 * vekt). Det er GALT her: et skip eller en glitrende solrefleks gir ofte en
 * MYE sterkere lokal gradient enn selve horisontlinja, og en vekting på
 * styrken ville da latt akkurat de punktene DOMINERE fittet FØR sigma-
 * klippingen får sjansen til å se dem som utliggere — utliggeren hadde vunnet
 * på egne premisser. `finnKantIKolonne` har allerede filtrert på `minKontrast`,
 * så alle punkter her er «gode nok»; sigma-klippingen i
 * `detekterHorisontLinje` er robusthetsmekanismen, ikke vektingen.
 */
function linjefit(punkter, cx) {
    const n = punkter.length;
    let sx = 0, sy = 0, sxx = 0, sxy = 0;
    for (const p of punkter) {
        const xr = p.x - cx;
        sx += xr;
        sy += p.ySub;
        sxx += xr * xr;
        sxy += xr * p.ySub;
    }
    const nevner = n * sxx - sx * sx;
    const m = Math.abs(nevner) > 1e-9 ? (n * sxy - sx * sy) / nevner : 0;
    const b = n > 0 ? (sy - m * sx) / n : 0;
    let sse = 0;
    for (const p of punkter) {
        const xr = p.x - cx;
        const resid = p.ySub - (m * xr + b);
        sse += resid * resid;
    }
    const sigmaPx = n > 0 ? Math.sqrt(sse / n) : 0;
    // SE(m) ≈ σ / √(Σ(x−x̄)²) — standard OLS-helningsvarians.
    const sxxSentrert = sxx - (sx * sx) / Math.max(n, 1);
    const mSigmaPxPerPx = sxxSentrert > 1e-9 ? sigmaPx / Math.sqrt(sxxSentrert) : 0;
    return { m, b, sigmaPx, mSigmaPxPerPx };
}
/**
 * Finn horisontlinja i et bilde: skann hver (eller hver n-te) kolonne etter
 * den sterkeste lysstyrke-overgangen (himmel er typisk mye lysere/kjøligere
 * enn hav), subpiksel-refiner den per kolonne, og fitt en rett linje gjennom
 * punktene med sigma-klipping (skip, fugl, glitter, skipstrafikk gir spredte
 * utliggere som IKKE skal trekke fittet — se testene for robusthet).
 *
 * Returnerer en begrunnet avvisning i stedet for et gjettet fit når bildet er
 * for lite, eller for få kolonner ga en kant over kontrastterskelen (jevn
 * dis, overeksponert bilde, natt) — å tvinge et linjefit gjennom støy ville
 * gitt et falskt presist pitch/roll-tall (§1).
 */
export function detekterHorisontLinje(bilde, opts = {}) {
    const steg = Math.max(1, Math.floor(opts.kolonneSteg ?? 1));
    const minKontrast = opts.minKontrast ?? 10;
    const sigmaKlipp = opts.sigmaKlipp ?? 3;
    const maksIterasjoner = opts.maksIterasjoner ?? 3;
    const w = bilde.breddePx, h = bilde.hoydePx;
    if (w < 10 || h < 10) {
        return { kind: 'avvist', grunn: 'bilde-for-lite', forklaring: `Bildet (${w}×${h}) er for lite til et pålitelig horisont-linjefit.` };
    }
    let punkter = [];
    const kolonnerForsokt = Math.floor(w / steg);
    for (let x = 0; x < w; x += steg) {
        const kant = finnKantIKolonne(luminansKolonne(bilde, x), minKontrast);
        if (kant)
            punkter.push({ x, ySub: kant.ySub, styrke: kant.styrke });
    }
    const minPunkter = Math.max(20, Math.floor(kolonnerForsokt * 0.15));
    if (punkter.length < minPunkter) {
        return {
            kind: 'avvist',
            grunn: punkter.length === 0 ? 'lav-kontrast' : 'for-faa-punkter',
            forklaring: `Bare ${punkter.length} av ${kolonnerForsokt} undersøkte kolonner ga en kant med gradient over ` +
                `terskelen ${minKontrast} — for få til et pålitelig linjefit (jevn dis, natt, eller overeksponert bilde?).`,
        };
    }
    const cx = w / 2;
    let fit = linjefit(punkter, cx);
    let antallUtelatt = 0;
    for (let i = 0; i < maksIterasjoner; i++) {
        const terskel = sigmaKlipp * Math.max(fit.sigmaPx, 0.05);
        const beholdt = punkter.filter((p) => Math.abs(p.ySub - (fit.m * (p.x - cx) + fit.b)) <= terskel);
        if (beholdt.length === punkter.length || beholdt.length < minPunkter)
            break;
        antallUtelatt += punkter.length - beholdt.length;
        punkter = beholdt;
        fit = linjefit(punkter, cx);
    }
    return {
        kind: 'ok',
        yVedSenterPx: fit.b,
        helningPxPerPx: fit.m,
        sigmaPx: fit.sigmaPx,
        helningSigmaPxPerPx: fit.mSigmaPxPerPx,
        antallPunkter: punkter.length,
        antallUtelatt,
    };
}
/**
 * Pitch og roll DIREKTE fra den fitta horisontlinja, i stedet for fra IMU-en.
 *
 * PITCH: horisonten har en KJENT verdenselevasjon på −dip (uansett hvor
 * kameraet peker — det er ren kurvatur+øyehøyde, se `horisontDipDeg`). Linja i
 * bildet ligger ved en vinkel `vinkelFraPikselRad(yVedSenterPx)` FRA
 * kameraets boresight. Siden `elevasjonFraPiksel = pitch + vinkelFraPiksel`
 * (kamerakalibrering.ts), og elevasjonen ved horisontraden SKAL være −dip, gir
 * det, løst for pitch: **pitch = −dip − vinkelFraPikselRad(yVedSenterPx)**.
 * Én linje, ingen ny formel — bare `vinkelFraPikselRad` brukt i revers, akkurat
 * som landemerke-resection bruker `vinkelFraPikselKolonne` i revers (§3).
 *
 * ROLL: en rulling om siktelinja roterer bildeinnholdet i PIKSELPLANET (samme
 * fysiske pikselstørrelse i x og y — det er sensorens ASPEKTFORHOLD, ikke
 * pikselformen, som gir hfov≠vfov). Linjas helning i pikselkoordinater ER
 * derfor `tan(rollDeg)` direkte, uten å gå via noe FOV-tall.
 *
 * ⚠ SAMME BØLGEVAKT SOM `maritimAvstand`: under `BOLGE_HOYDE_FORHOLD_MIN`×
 * signifikant bølgehøyde er selve horisontFITTET bølgekorrupt (bølgetopper,
 * ikke middelvann), ikke bare mindre presist — avvist, ikke bare mer usikkert.
 */
export function pitchRollFraHorisontlinje(k, linje, hoydeM, opts = {}) {
    if (!(hoydeM > 0)) {
        return { kind: 'avvist', grunn: 'ugyldig-inndata', forklaring: 'Øyehøyde må være > 0 for å regne dip-vinkelen.' };
    }
    const Hs = opts.bolgehoydeSignifikantM;
    if (Hs !== undefined && Hs > 0 && hoydeM < BOLGE_HOYDE_FORHOLD_MIN * Hs) {
        return {
            kind: 'avvist',
            grunn: 'bolger-for-store',
            forklaring: `Øyehøyde ${hoydeM.toFixed(1)} m er under ${BOLGE_HOYDE_FORHOLD_MIN}× signifikant bølgehøyde ` +
                `(${Hs.toFixed(1)} m) — samme terskel som \`maritimAvstand\` (maalpeking.ts). Horisontfittet er da ` +
                'bølgekorrupt, ikke bare mindre presist, og pitch/roll ut av det kan ikke stoles på.',
        };
    }
    const vinkelVedSenter = vinkelFraPikselRad(k, linje.yVedSenterPx);
    if (vinkelVedSenter === null || k.hoydePx === undefined || k.vfovDeg === undefined) {
        return {
            kind: 'avvist',
            grunn: 'ugyldig-inndata',
            forklaring: 'Kalibreringen mangler vfovDeg/hoydePx — ingen vinkel kan regnes for horisontraden.',
        };
    }
    const medRefraksjon = opts.medRefraksjon ?? true;
    const dipDeg = horisontDipDeg(hoydeM, medRefraksjon);
    const pitchDeg = -dipDeg - vinkelVedSenter;
    const rollDeg = (Math.atan(linje.helningPxPerPx) * 180) / Math.PI;
    const cy = k.hoydePx / 2;
    const f = cy / Math.tan((k.vfovDeg * Math.PI) / 360);
    // Småvinkel-tilnærming dθ ≈ dy/f — konsistent med selve projeksjonen
    // (`vinkelFraPikselRad`), og god nok for et residual på under noen piksler.
    const pitchSigmaDeg = (linje.sigmaPx / f) * (180 / Math.PI);
    const rollSigmaDeg = (linje.helningSigmaPxPerPx / (1 + linje.helningPxPerPx ** 2)) * (180 / Math.PI);
    return {
        kind: 'ok',
        pitchDeg,
        rollDeg,
        pitchSigmaDeg,
        rollSigmaDeg,
        dipDeg,
        forklaring: `Horisont-linjefit: y=${linje.yVedSenterPx.toFixed(2)}px ved senterkolonne, helning ` +
            `${linje.helningPxPerPx.toFixed(4)} px/px (${linje.antallPunkter} punkter, σ=${linje.sigmaPx.toFixed(2)}px) → ` +
            `pitch ${pitchDeg.toFixed(3)}° ± ${pitchSigmaDeg.toFixed(3)}°, roll ${rollDeg.toFixed(3)}° ± ${rollSigmaDeg.toFixed(3)}° ` +
            `(dip ${dipDeg.toFixed(3)}° ved øyehøyde ${hoydeM.toFixed(1)} m). Til sammenligning: telefonens IMU-pitch ` +
            'har typisk 1,7° feil — se advarselen over `estimerMaal` i maalestimat.ts.',
    };
}
// ═══════════════════════════════════════════════════════════════════════════
// 3. MEDIAN OVER RAMMER — bølgetopper flytter horisonten SYSTEMATISK
// ═══════════════════════════════════════════════════════════════════════════
function medianAvTall(tall) {
    const sortert = [...tall].sort((a, b) => a - b);
    const n = sortert.length;
    const mid = Math.floor(n / 2);
    return n % 2 === 0 ? (sortert[mid - 1] + sortert[mid]) / 2 : sortert[mid];
}
/**
 * Median-filtrer et sett med detekterte horisontlinjer (typisk ~1 s med
 * rammer). Bølgetopper flytter det ENKELTBILDE-detekterte horisontfittet
 * systematisk (kortets egen formulering) — medianen over et vindu av rammer
 * kansellerer akkurat den periodiske forskyvningen en gjennomsnittsberegning
 * ikke ville (ett ekstremt utfall trekker et snitt, ikke en median).
 *
 * Hvert felt medianfiltreres UAVHENGIG (ikke f.eks. først summert til én
 * verdi) — samme prinsipp som `sammensattOyehoyde` i maalpeking.ts holder
 * leddene atskilt: en median av `yVedSenterPx` og en median av
 * `helningPxPerPx` er meningsfulle hver for seg, en median av et sammensatt
 * tall ville ikke vært det.
 */
export function medianfiltrerHorisont(rammer) {
    if (rammer.length === 0)
        return null;
    return {
        yVedSenterPx: medianAvTall(rammer.map((r) => r.yVedSenterPx)),
        helningPxPerPx: medianAvTall(rammer.map((r) => r.helningPxPerPx)),
        sigmaPx: medianAvTall(rammer.map((r) => r.sigmaPx)),
        helningSigmaPxPerPx: medianAvTall(rammer.map((r) => r.helningSigmaPxPerPx)),
        antallPunkter: Math.round(medianAvTall(rammer.map((r) => r.antallPunkter))),
        antallUtelatt: Math.round(medianAvTall(rammer.map((r) => r.antallUtelatt))),
    };
}
/**
 * Glidende vindu av de siste N detekterte horisontlinjene, til median-
 * filtrering over tid.
 *
 * ⚠ ÆRLIG BEGRENSNING (§1): INGEN LEVENDE BILDESTRØM FINNES Å FÔRE DENNE MED
 * I DAG. `malpeking.ts` (COP-panelet) behandler ETT stillbilde operatøren
 * laster opp — ikke en video-/kamerastrøm med flere rammer i sekundet. Denne
 * klassen er derfor bygget og testet FRITTSTÅENDE (se testfila, som fôrer den
 * syntetiske rammer for hånd), klar til å kobles til en ekte rammestrøm den
 * dagen en finnes i denne kodebasen — i stedet for å late som en strøm som
 * ikke eksisterer, eller late som funksjonen ikke er verdt å bygge før den
 * gjør det.
 */
export class HorisontRammebuffer {
    maksRammer;
    rammer = [];
    constructor(maksRammer) {
        this.maksRammer = maksRammer;
        if (!(maksRammer > 0))
            throw new Error('maksRammer må være > 0');
    }
    leggTil(linje) {
        this.rammer.push(linje);
        if (this.rammer.length > this.maksRammer)
            this.rammer.shift();
    }
    antallRammer() {
        return this.rammer.length;
    }
    medianLinje() {
        return medianfiltrerHorisont(this.rammer);
    }
    tom() {
        this.rammer = [];
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// 4. ERSTATT IMU-PITCH — der `estimerMaal` ellers ville tatt IMU-en på ordet
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Bytt IMU-pitchen i en `Sensorpose` med horisont-fittets pitch, NÅR fittet er
 * gyldig. Kortets krav er ordrett at IMU-pitch «ikke er en måling, det er en
 * gjetning» over vann (se advarselen over `estimerMaal` i maalestimat.ts), og
 * at horisontfittet er den riktige løsningen der det finnes — denne funksjonen
 * er selve erstatningen, som en ren, testbar transformasjon av posen.
 *
 * ⚠ ÆRLIG STATUS (§1): INGEN KALLESTED BRUKER DETTE I DAG, OG DET ER SJEKKET,
 * IKKE ANTATT. Det ENESTE stedet `estimerMaal` faktisk kalles med en
 * sensorpose i denne kodebasen er `rammefotavtrykk.ts`, og DER er `pitchDeg`
 * en drone-GIMBAL-ENKODERMÅLING (sub-grad), ikke en telefons IMU — nøyaktig
 * situasjonen `rammefotavtrykk.ts` sin egen kommentar sier advarselen IKKE
 * gjelder for («det er derfor dette virker for droner og ikke for et
 * håndholdt kamera over vann»). Planen nevnt i `services/hub/src/server.ts`
 * («COP-en regner estimatet lokalt» for GNÅ-bilder med DEM-en den har) er en
 * ÅPEN EIERBESLUTNING, ikke bygget kode — det finnes derfor IKKE et kallsted
 * i dag som fôrer en håndholdt telefons IMU-pitch inn i `estimerMaal` for et
 * sjømål. Funksjonen er likevel skrevet og testet FULLT UT her, klar til å
 * brukes i det øyeblikket et slikt kallsted bygges, i stedet for å late som
 * det allerede finnes ett.
 *
 * Uendret pose returneres når horisontresultatet er en avvisning — en
 * avvist automatikk skal ALDRI stille falle tilbake på en enda dårligere
 * gjetning enn den som allerede var der (samme prinsipp som resten av fila:
 * avvis synlig, gjett aldri).
 */
export function sensorposeMedHorisontPitch(pose, horisont) {
    if (horisont.kind !== 'ok')
        return pose;
    return { ...pose, pitchDeg: horisont.pitchDeg, pitchSigmaDeg: horisont.pitchSigmaDeg };
}
//# sourceMappingURL=horisontdeteksjon.js.map